import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faHeart, faShoppingCart, faBell, faSignOutAlt, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FaChevronRight, FaUser } from "react-icons/fa";

const Sidebar = ({
	isOpen,
	onClose,
	isAuthenticated,
	user,
	categories,
	loadingCategories,
	wishlistCount,
	cartCount,
	unreadCount,
	location,
	onLoginClick,
	onLogout,
	navigate
}) => {
	const getLinkClass = (path) => {
		return location.pathname === path ? "bg-blue-50 text-blue-600" : "";
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 lg:hidden">
			{/* Overlay - Dark coffee tint with blur */}
			<div
				className="fixed inset-0 bg-[#1A1614]/60 backdrop-blur-sm transition-opacity duration-300"
				onClick={onClose}
			></div>

			{/* Sidebar Content - Right Aligned & Scrollable */}
			<div className="fixed inset-y-0 right-0 w-[85%] max-w-sm bg-[#FDFBF7] shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col">

				{/* Subtle wood grain texture overlay */}
				<div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/pinstriped-suit.png')]"></div>

				{/* Header - Fixed */}
				<div className="p-6 flex justify-end items-center border-b border-stone-200 relative z-10 bg-[#FDFBF7]">
					<button
						onClick={onClose}
						className="w-10 h-10 flex items-center justify-center border border-stone-200 text-stone-400 active:text-stone-900 active:border-stone-900 transition-all"
					>
						<FontAwesomeIcon icon={faTimes} />
					</button>
				</div>

				{/* Scrollable Body */}
				<div className="flex-1 overflow-y-auto relative z-10 custom-scrollbar">
					<div className="p-6">

						{/* Profile/Auth Section */}
						<div className="mb-8 pb-8 border-b border-stone-200">
							{isAuthenticated ? (
								<Link
									to="/profile"
									className="flex items-center space-x-4 group"
									onClick={onClose}
								>
									{/* Chiseled Profile Box */}
									<div className="bg-white border border-stone-200 flex items-center justify-center w-14 h-14 shadow-sm group-hover:border-[#7A5C33] transition-colors">
										<FontAwesomeIcon icon={FaUser} className="text-stone-300 group-hover:text-[#7A5C33]" size="lg" />
									</div>
									<div>
										<p className="font-serif font-bold text-lg text-stone-800 leading-tight">{user?.name || "Artisan User"}</p>
										<p className="text-[#A38A64] text-[10px] uppercase tracking-widest font-bold">View Studio Profile</p>
									</div>
								</Link>
							) : (
								<button
									className="flex items-center space-x-4 w-full group text-left"
									onClick={onLoginClick}
								>
									<div className="bg-white border border-stone-200 flex items-center justify-center w-14 h-14 shadow-sm">
										<FontAwesomeIcon icon={faSignOutAlt} className="text-stone-300" size="lg" />
									</div>
									<div>
										<p className="font-serif font-bold text-lg text-stone-800">Login / Signup</p>
										<p className="text-[#A38A64] text-[10px] uppercase tracking-widest font-bold">Join the Community</p>
									</div>
								</button>
							)}
						</div>

						{/* Categories Section */}
						<div className="mb-10">
							<h3 className="text-[#A38A64] text-[10px] font-bold uppercase tracking-[0.2em] mb-4">Collections</h3>
							{loadingCategories ? (
								<div className="space-y-3">
									{[...Array(5)].map((_, i) => (
										<div key={i} className="h-10 w-full bg-stone-100/50 animate-pulse border border-stone-100"></div>
									))}
								</div>
							) : (
								<div className="space-y-1">
									{categories.map((category) => (
										<Link
											key={category.id}
											to={category.path}
											className={`flex items-center py-3 font-serif text-lg text-stone-700 active:text-[#7A5C33] border-b border-stone-50 last:border-0 ${getLinkClass(category.path)}`}
											onClick={onClose}
										>
											<span>{category.name}</span>
											<FontAwesomeIcon icon={FaChevronRight} className="ml-auto text-[10px] text-stone-300" />
										</Link>
									))}
								</div>
							)}
						</div>

						{/* Account Links */}
						<div className="mb-10">
							<h3 className="text-[#A38A64] text-[10px] font-bold uppercase tracking-[0.2em] mb-4">Your Studio</h3>
							<div className="space-y-2">
								{[
									{ to: "/wishlist", label: "Wishlist", icon: faHeart, count: wishlistCount, show: isAuthenticated },
									{ to: "/cart", label: "Shopping Cart", icon: faShoppingCart, count: cartCount, show: isAuthenticated },
									{ to: "/notifications", label: "Notifications", icon: faBell, count: unreadCount, show: isAuthenticated }
								].map((item, idx) => item.show && (
									<Link
										key={idx}
										to={item.to}
										className="flex items-center space-x-4 p-3 bg-white border border-stone-100 text-stone-600 active:bg-stone-50 transition-colors"
										onClick={onClose}
									>
										<FontAwesomeIcon icon={item.icon} className="w-5 text-stone-300" />
										<span className="font-serif text-sm">{item.label}</span>
										{item.count > 0 && (
											<span className="ml-auto bg-[#7A5C33] text-white text-[10px] font-bold px-2 py-0.5 rounded-none">
												{item.count}
											</span>
										)}
									</Link>
								))}
							</div>
						</div>

						{/* Become a Seller CTA */}
						{isAuthenticated && (
							<div className="mt-4 pt-4 border-t border-stone-200">
								{/* <Link
									to="/become-seller"
									className="flex items-center justify-center space-x-3 p-4 bg-[#7A5C33] text-white shadow-md active:bg-stone-800 transition-all font-bold text-xs uppercase tracking-[0.2em]"
									onClick={onClose}
								>
									<FontAwesomeIcon icon={faPlus} />
									<span>Become a Seller</span>
								</Link> */}

								<button
									className="flex items-center justify-center space-x-2 w-full py-6 text-stone-400 text-xs uppercase tracking-widest font-bold hover:text-red-700 transition-colors"
									onClick={onLogout}
								>
									<FontAwesomeIcon icon={faSignOutAlt} />
									<span>Logout</span>
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Sidebar;